/*
 * What the week and month overviews share: what a card says, its trade tag, its "Flytta till"
 * menu, the hours summary, the snackbar after a move, the drag that moves a card, and the shades a
 * day is drawn in. Each view lays these out its own way.
 */
import React, { useState } from "react";
import { Box, Button, Chip, IconButton, ListSubheader, Menu, MenuItem, Snackbar, Tooltip, Typography, useTheme } from "@mui/material";
import { blueGrey, brown, cyan, deepPurple, indigo, lime, pink, teal } from "@mui/material/colors";
import { alpha, darken, lighten } from "@mui/material/styles";
import EventRepeatIcon from "@mui/icons-material/EventRepeat";
import ReportIcon from "@mui/icons-material/Report";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { MouseSensor, TouchSensor, pointerWithin, useSensor, useSensors } from "@dnd-kit/core";
import { isToday } from "date-fns";
import { useIntl } from "react-intl";
import { EMPTY_VALUE, formatDurationHours } from "../../cmms/cmmsFormat";
import { getMenuSx } from "../../cmms/assetHierarchyStyles";
import {
  WorkOrderPriority,
  WorkOrderStatus,
  WorkOrderType,
  getPriorityBadge,
  getStatusBadge,
  getTypeBadge,
} from "../workOrderEnums";
import { getWeekInfo } from "../workOrderWeek";
import { renderBadge } from "./WorkOrdersDataGrid";
import messages from "../messages";

// The weekday with the month, which reads in the same order in both languages: "mån 19 okt.",
// "Mon, Oct 19". Without the month, English puts the number first ("19 Mon").
export const DAY_OPTIONS = { weekday: "short", day: "numeric", month: "short" };
export const LONG_DAY_OPTIONS = { weekday: "long", day: "numeric", month: "long" };
export const TIME_OPTIONS = { hour: "2-digit", minute: "2-digit" };

/*
 * When a press on a card turns into a drag. With a mouse, once it has moved a few pixels, so a
 * click still opens the card. On a touch screen, once it has been held still for a moment, so a
 * swipe across the days still scrolls them.
 */
const MOUSE_DRAG = { distance: 6 };
const TOUCH_DRAG = { delay: 250, tolerance: 5 };

/*
 * dnd-kit reads each step of a drag out loud, in English and by id ("Draggable item 12 was
 * dropped over droppable area 3"). The card's menu is how to move a card without seeing it, and
 * the snackbar says where a dropped card went, so the drag itself says nothing.
 */
const silent = () => undefined;
const DRAG_ACCESSIBILITY = {
  announcements: { onDragStart: silent, onDragOver: silent, onDragEnd: silent, onDragCancel: silent },
};

/*
 * The akut kinds by shape as well as by colour, since red and orange are the pair a red-green
 * colour-blind reader cannot tell apart: a stop sign for a stoppage, filled so it is the first
 * thing seen in a day, and a warning triangle for a problem. Preventive work has none. It is the
 * quiet default the akut work stands out from.
 */
export const TYPE_ICONS = {
  [WorkOrderType.MachineBreakdown]: ReportIcon,
  [WorkOrderType.MachineProblem]: WarningAmberIcon,
};

/*
 * A card's badges leave out the usual: low or medium priority and an open status are most of a
 * week, and a badge on every card would say nothing. The drawer and the list still show them.
 */
const showsPriority = (priority) => priority === WorkOrderPriority.High || priority === WorkOrderPriority.Critical;
const showsStatus = (status) => status !== WorkOrderStatus.Open;

const clampSx = (lines) => ({
  display: "-webkit-box",
  overflow: "hidden",
  overflowWrap: "anywhere",
  WebkitBoxOrient: "vertical",
  WebkitLineClamp: lines,
});

// Read out, not shown: what a card says to the eye in another way. In px, since sx reads a width
// of 1 as 100% and a margin of -1 as a spacing step.
export const SCREEN_READER_ONLY_SX = {
  border: 0,
  clip: "rect(0 0 0 0)",
  height: "1px",
  margin: "-1px",
  overflow: "hidden",
  padding: 0,
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
};

const isMidnight = (date) => date.getHours() === 0 && date.getMinutes() === 0;

/*
 * The day a card is on already says the day, so a card says the time, where the time means
 * something: when an akut order came in, or when planned work is set to start. A preventive order
 * the sweep raised carries the hour of the nightly run, and a projection has a day but no hour, so
 * neither gets one. A planned start at midnight is a day with no hour too. That is what moving an
 * order with no start gives it (see getMovedStart). A late card sits where there is no day, so it
 * says its own date instead.
 */
export const getCardWhen = (intl, row, acute) => {
  if (row.carriedOver) return intl.formatDate(row.placedAt, DAY_OPTIONS);
  if (row.projected) return null;
  if (row.workOrder.plannedStartDate) return isMidnight(row.placedAt) ? null : intl.formatTime(row.placedAt, TIME_OPTIONS);
  if (acute) return intl.formatTime(row.placedAt, TIME_OPTIONS);
  return null;
};

/*
 * The shades a day is drawn in: the weekend, today, and the late work from before the days on
 * screen.
 */
export const getCalendarShades = (theme, palette) => {
  const isDark = theme.palette.mode === "dark";
  return {
    // surfaceMuted is the surface itself in dark mode, where a weekend shaded with it would not show.
    weekend: isDark ? palette.surfaceHover : palette.surfaceMuted,
    today: palette.selected,
    // The warning colour the late chip on each card had before the late column took over saying it.
    late: alpha(theme.palette.warning.main, isDark ? 0.12 : 0.08),
    lateText: theme.palette.warning.main,
  };
};

// The days in order, under the ISO week each is in.
const groupByWeek = (days) => days.reduce((weeks, day) => {
  const { weekNumber, start } = getWeekInfo(day);
  const last = weeks[weeks.length - 1];
  if (last && last.start.getTime() === start.getTime()) {
    last.days.push(day);
  } else {
    weeks.push({ weekNumber, start, days: [day] });
  }
  return weeks;
}, []);

// The menu's own heading, which the week headings stick under as the menu scrolls.
const MOVE_MENU_HEADING_HEIGHT = 32;

/*
 * A card's "Flytta till" button and its menu of days, for a manager. It is the way to move a card
 * that does not need a drag: from the keyboard, with a screen reader, or for a hand that cannot
 * hold a drag steady.
 *
 * It sits beside the card's own button, not inside it, since a button inside a button is a
 * control a screen reader cannot reach properly. It keeps out of the way until the card is pointed
 * at or tabbed to (MOVE_HOST_SX, on what holds both). On a touch screen, which cannot point at
 * anything, it is always there.
 *
 * Days from more than one week, as a month has, are put under their week, and the menu scrolls
 * rather than running off the screen. A week's days need neither.
 *
 * dense is for a one-line card, which has no room kept for the button. The button covers the end
 * of the title while it shows, on the card's own surface so the title does not show through it.
 */
export function MoveMenu({ row, targets, onMove, palette, dense = false }) {
  const intl = useIntl();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const buttonId = `week-move-button-${row.key}`;
  const menuId = `week-move-menu-${row.key}`;
  const weeks = groupByWeek(targets);
  const byWeek = weeks.length > 1;

  const handlePick = (day) => {
    setAnchorEl(null);
    onMove(row, day);
  };

  const renderDay = (day) => {
    const dayName = intl.formatDate(day, LONG_DAY_OPTIONS);
    return (
      <MenuItem key={day.getTime()} onClick={() => handlePick(day)}>
        {isToday(day) ? intl.formatMessage(messages.weekMoveTargetToday, { day: dayName }) : dayName}
      </MenuItem>
    );
  };

  return (
    <>
      <Tooltip title={intl.formatMessage(messages.weekMoveTo)}>
        <IconButton
          id={buttonId}
          className="weekCardMove"
          size="small"
          aria-label={intl.formatMessage(messages.weekMoveAria, { title: row.workOrder.title })}
          aria-haspopup="menu"
          aria-controls={open ? menuId : undefined}
          aria-expanded={open ? "true" : undefined}
          onClick={(event) => setAnchorEl(event.currentTarget)}
          sx={{
            position: "absolute",
            top: 2,
            right: 2,
            p: 0.25,
            color: palette.mutedText,
            "&:hover": { backgroundColor: palette.buttonHover, color: palette.text },
            "@media (hover: hover)": { opacity: open ? 1 : 0 },
            ...(dense
              ? {
                backgroundColor: palette.surface,
                borderRadius: 0,
                // The hover shade is see-through, so it is laid over the surface rather than in its place.
                "&:hover": {
                  backgroundColor: palette.surface,
                  backgroundImage: `linear-gradient(${palette.buttonHover}, ${palette.buttonHover})`,
                  color: palette.text,
                },
              }
              : {}),
          }}
        >
          <EventRepeatIcon sx={{ fontSize: dense ? 16 : 18 }} />
        </IconButton>
      </Tooltip>
      <Menu
        id={menuId}
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        MenuListProps={{
          "aria-labelledby": buttonId,
          dense: true,
          subheader: (
            <ListSubheader
              sx={{
                backgroundColor: palette.surface,
                color: palette.mutedText,
                lineHeight: `${MOVE_MENU_HEADING_HEIGHT}px`,
              }}
            >
              {intl.formatMessage(messages.weekMoveTo)}
            </ListSubheader>
          ),
        }}
        PaperProps={{ sx: { ...getMenuSx(palette), ...(byWeek ? { maxHeight: 360 } : {}) } }}
      >
        {byWeek
          ? weeks.flatMap((week) => [
            <ListSubheader
              key={`week-${week.start.getTime()}`}
              sx={{
                backgroundColor: palette.surface,
                borderTop: `1px solid ${palette.border}`,
                color: palette.text,
                fontWeight: 700,
                lineHeight: "28px",
                top: MOVE_MENU_HEADING_HEIGHT,
              }}
            >
              {intl.formatMessage(messages.weekMoveWeekHeading, { weekNumber: week.weekNumber })}
            </ListSubheader>,
            ...week.days.map(renderDay),
          ])
          : targets.map(renderDay)}
      </Menu>
    </>
  );
}

// On what holds a card and its MoveMenu: the menu's button shows while the card is pointed at or
// has focus.
export const MOVE_HOST_SX = {
  position: "relative",
  "&:hover .weekCardMove, &:focus-within .weekCardMove": { opacity: 1 },
};

export const getCardSx = (projected, typeColor, palette) => ({
  backgroundColor: palette.surface,
  border: `1px solid ${projected ? palette.buttonBorder : palette.border}`,
  borderLeft: `3px solid ${typeColor}`,
  borderRadius: 0,
  // The type's edge stays solid on a projection; the dashes are its other three sides.
  borderStyle: projected ? "dashed dashed dashed solid" : "solid",
  display: "block",
  minWidth: 0,
  px: 1,
  py: 0.75,
});

/*
 * What a card says: what the order is, its badges, then when, where and who.
 *
 * Akut and planned share the day, so the type is on the card's left edge in the colours the list
 * gives it: red for a stoppage, orange for a problem, blue for preventive work. An akut card also
 * has its type's icon before the title and spells the type out in its details, so the icon never
 * has to be learnt. Every other card is preventive maintenance, and saying so on each would be
 * noise.
 *
 * A projected card is an order a schedule has not raised yet. It has no status, so its badge says
 * it is upcoming.
 *
 * The trade's tag leads the details where the card is told to show it (tradeTagById). A screen
 * reader hears the trade there in full.
 */
export function WeekCardContent({ row, acute, hasMoveButton, assetNodeNameById, userNameById, tradeTagById, palette }) {
  const intl = useIntl();
  const { workOrder, placedAt, projected } = row;
  const type = getTypeBadge(workOrder.workOrderType, intl);
  const TypeIcon = TYPE_ICONS[workOrder.workOrderType];
  const tradeTag = workOrder.tradeId != null ? tradeTagById?.get(workOrder.tradeId) : undefined;
  const assignee = workOrder.assignedResourceId
    ? (userNameById?.get(workOrder.assignedResourceId) ?? `#${workOrder.assignedResourceId}`)
    : null;

  const details = [
    acute ? type.label : null,
    getCardWhen(intl, row, acute),
    // A projection names its own node, for a node the tree does not have yet.
    assetNodeNameById?.get(workOrder.assetNodeId) ?? workOrder.assetNodeName ?? `#${workOrder.assetNodeId}`,
    assignee,
  ].filter(Boolean);

  const priorityBadge = showsPriority(workOrder.priority) ? renderBadge(getPriorityBadge(workOrder.priority, intl)) : null;
  // A projection has no status, so its badge is always there: it says the order does not exist yet.
  const statusBadge = projected
    ? (
      <Tooltip
        title={workOrder.isOverdue
          ? intl.formatMessage(messages.weekProjectedOverdueTooltip)
          : intl.formatMessage(messages.weekProjectedTooltip, { day: intl.formatDate(placedAt, LONG_DAY_OPTIONS) })}
      >
        <Chip
          size="small"
          color="info"
          variant="outlined"
          label={intl.formatMessage(messages.weekProjected)}
          sx={{ fontWeight: 700 }}
        />
      </Tooltip>
    )
    : showsStatus(workOrder.status) ? renderBadge(getStatusBadge(workOrder.status, intl)) : null;

  return (
    <>
      {/* Room on the right for the move button, which sits over the card's corner. */}
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5, pr: hasMoveButton ? 2.5 : 0 }}>
        {TypeIcon &&
          <TypeIcon sx={{ color: type.bg, flexShrink: 0, fontSize: 18, mt: "1px" }} />
        }
        <Typography
          variant="body2"
          sx={{ ...clampSx(2), color: palette.text, fontWeight: 700, minWidth: 0 }}
          title={workOrder.title}
        >
          {workOrder.title}
        </Typography>
      </Box>
      {/* A badge too long for the column is cut short rather than pushing the column wider. */}
      {(priorityBadge || statusBadge) &&
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5, "& .MuiChip-root": { maxWidth: "100%" } }}>
          {priorityBadge}
          {statusBadge}
        </Box>
      }
      <Typography variant="caption" sx={{ ...clampSx(2), color: palette.mutedText, mt: 0.5 }}>
        {tradeTag &&
          <>
            <TradeTag tag={tradeTag} />
            <Box component="span" sx={SCREEN_READER_ONLY_SX}>
              {`${intl.formatMessage(messages.monthTooltipTrade, { name: tradeTag.name })} ·`}
            </Box>
            {" "}
          </>
        }
        {details.join(" · ")}
      </Typography>
    </>
  );
}

/*
 * The trade tags' colours, in the order getTradeTags hands them out. None is a colour a card
 * already uses for something else: the types' red, orange and blue, "Pågår"'s purple, or the green
 * of today and of done work. Teal and cyan, and indigo and deep purple, are the nearest pairs, so
 * each pair is far apart in the order, and only a company with seven trades or more gets both.
 *
 * Dark on a light card and pale on a dark one, at 4.5:1 or better against the card with the tag's
 * tint behind it. Lime's darkest shade is too light to read on a light card, so it is darkened, to
 * olive.
 */
const TRADE_TAG_COLORS = [
  { light: teal[800], dark: teal[200] },
  { light: pink[800], dark: pink[200] },
  { light: indigo[600], dark: indigo[200] },
  { light: brown[600], dark: brown[200] },
  { light: darken(lime[900], 0.2), dark: lime[200] },
  { light: blueGrey[700], dark: blueGrey[200] },
  { light: deepPurple[600], dark: deepPurple[200] },
  { light: cyan[900], dark: cyan[200] },
];

// A dark card is a mid grey, which the 200 shades are not pale enough to read on as text. They
// still draw the border and the tint, so the colour is seen.
const getTradeTagColors = (colorIndex, isDark) => {
  const { light, dark } = TRADE_TAG_COLORS[colorIndex % TRADE_TAG_COLORS.length];
  return isDark
    ? { text: lighten(dark, 0.5), border: dark, background: alpha(dark, 0.16) }
    : { text: light, border: light, background: alpha(light, 0.1) };
};

/*
 * Which trade an order is for: the trade's short name in its colour (getTradeTags). The name says
 * it and the colour helps the eye, so the tag needs no legend and still reads without its colour.
 * Tinted rather than filled, so it is not taken for one of the card's badges. Cut short past eight
 * letters or so, which only a name grown apart from another's gets to.
 *
 * For the eye only: the card reads the trade's full name out where it reads best.
 */
export function TradeTag({ tag }) {
  const theme = useTheme();
  const colors = getTradeTagColors(tag.colorIndex, theme.palette.mode === "dark");
  return (
    <Typography
      component="span"
      variant="caption"
      aria-hidden
      sx={{
        backgroundColor: colors.background,
        border: `1px solid ${colors.border}`,
        color: colors.text,
        display: "inline-block",
        flexShrink: 0,
        fontWeight: 700,
        lineHeight: "14px",
        maxWidth: "10ch",
        overflow: "hidden",
        px: 0.5,
        textOverflow: "ellipsis",
        verticalAlign: "middle",
        whiteSpace: "nowrap",
      }}
    >
      {tag.label}
    </Typography>
  );
}

/*
 * The hours a group or a period is estimated to take, and how many orders the sum leaves out.
 *
 * The left-out count is shown whenever there is one, so "60h" is never read as the whole load when
 * it is not. A group nobody has estimated shows only the count. Nothing at all when there is
 * nothing to say, which the view decides for what it shows as a whole (its showEstimates).
 */
export function EstimateSummary({ estimatedHours, withoutEstimateCount, palette }) {
  const intl = useIntl();
  const hasSum = estimatedHours !== null;
  if (!hasSum && withoutEstimateCount === 0) return null;

  return (
    <Typography variant="body2" component="span" sx={{ color: palette.mutedText }}>
      {hasSum &&
        <Box component="span" sx={{ color: palette.text, fontWeight: 700 }}>
          {intl.formatMessage(messages.weekEstimatedHours, { hours: formatDurationHours(intl, estimatedHours) })}
        </Box>
      }
      {hasSum && withoutEstimateCount > 0 && " · "}
      {withoutEstimateCount > 0 &&
        <Tooltip title={intl.formatMessage(messages.weekWithoutEstimateTooltip)}>
          <Box component="span" sx={{ cursor: "help", textDecoration: "underline dotted" }}>
            {intl.formatMessage(messages.weekWithoutEstimate, { count: withoutEstimateCount })}
          </Box>
        </Tooltip>
      }
    </Typography>
  );
}

/*
 * A day's hours, or a week row's in the month. Marked whenever orders are missing from them, so a
 * day's "6h" is not read as the whole day. With orders and no estimates it is a dash with the same
 * mark rather than a misleading "0h".
 */
export function DayHours({ estimatedHours, withoutEstimateCount }) {
  const intl = useIntl();
  const hoursText = estimatedHours !== null ? formatDurationHours(intl, estimatedHours) : EMPTY_VALUE;
  if (withoutEstimateCount === 0) return hoursText;

  return (
    <Tooltip title={intl.formatMessage(messages.weekDayWithoutEstimateTooltip, { count: withoutEstimateCount })}>
      <Box component="span" sx={{ cursor: "help", textDecoration: "underline dotted" }}>{hoursText}</Box>
    </Tooltip>
  );
}

/*
 * What was moved where, with a way back, from useWorkOrderMove's notice. A click elsewhere leaves
 * it be, since the next thing done after a move may well be another move. A new move replaces it,
 * and Undo always takes back the last one. Undo is left out where the move cannot be taken back
 * (getUndoStart).
 */
export function MoveUndoSnackbar({ notice, onClose, onUndo }) {
  const intl = useIntl();
  const { move } = notice;
  return (
    <Snackbar
      key={move?.key}
      open={notice.open}
      autoHideDuration={8000}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      onClose={(_event, reason) => {
        if (reason !== "clickaway") onClose();
      }}
      message={move
        ? intl.formatMessage(messages.weekMoveDone, {
          title: move.workOrder.title,
          day: intl.formatDate(move.day, LONG_DAY_OPTIONS),
        })
        : ""}
      action={move?.undoStart
        ? (
          <Button color="inherit" size="small" onClick={onUndo} sx={{ fontWeight: 700 }}>
            {intl.formatMessage(messages.weekMoveUndo)}
          </Button>
        )
        : null}
    />
  );
}

/*
 * Dragging a card to a day: the same move as the menu's, to the same days. The card being dragged
 * and where it may go, worked out once as it is picked up, so each day can say whether it takes
 * the drop (isDropTarget). A drop anywhere else, or Esc, leaves the card where it was.
 *
 * getTargets(row) gives the days a row may go to, and onMove(row, day) moves it. A card's
 * useDraggable data is { row } and whatever else the view needs back, which drag keeps.
 *
 * Days are found by where the pointer is rather than by how much of the card overlaps them, since
 * a card is about as wide as a day and would be over two of them at once.
 *
 * As { drag, dndProps }: the card being dragged with its targets, or null, and what the
 * DndContext takes.
 */
export const useDragToMove = ({ getTargets, onMove }) => {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: MOUSE_DRAG }),
    useSensor(TouchSensor, { activationConstraint: TOUCH_DRAG }),
  );
  const [drag, setDrag] = useState(null);

  const onDragStart = ({ active }) => {
    const data = active.data.current;
    setDrag({ ...data, targets: getTargets(data.row) });
  };

  const onDragEnd = ({ over }) => {
    const dropped = drag;
    setDrag(null);
    const day = over?.data.current?.date;
    if (dropped && day) onMove(dropped.row, day);
  };

  return {
    drag,
    dndProps: {
      sensors,
      collisionDetection: pointerWithin,
      accessibility: DRAG_ACCESSIBILITY,
      onDragStart,
      onDragEnd,
      onDragCancel: () => setDrag(null),
    },
  };
};

// A day the dragged card can go to: one of its targets, and in its own panel where the view has
// panels (groupKey).
export const isDropTarget = (drag, date, groupKey) => Boolean(drag)
  && drag.groupKey === groupKey
  && date !== null
  && drag.targets.some((day) => day.getTime() === date.getTime());

// While a card is dragged, a day it can be dropped on is outlined, and the one under the pointer is
// filled in as well.
export const getDropTargetSx = (isOver, palette) => ({
  outline: `2px ${isOver ? "solid" : "dashed"} ${isOver ? palette.accent : palette.selectedBorder}`,
  outlineOffset: -3,
  ...(isOver ? { backgroundColor: palette.selected } : {}),
});
